import { Box, Typography } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import sciatique from "../assets/newImg/sciatique.png";
import SEO, { SITE_URL } from "../components/SEO";
import { SX } from "../sx";
import { C, F, R, S, maxW, trans } from "../tokens";

const SYMPTOMES = [
  {
    title: "Douleur traçante",
    desc: "De la fesse jusqu'au mollet ou au pied, suivant le trajet du nerf sciatique (L4, L5 ou S1).",
  },
  {
    title: "Brûlure ou décharge",
    desc: "Sensation électrique le long de la jambe, souvent aggravée en position assise prolongée.",
  },
  {
    title: "Engourdissement",
    desc: "Fourmillements ou perte de sensibilité dans la jambe, le pied ou les orteils.",
  },
  {
    title: "Faiblesse musculaire",
    desc: "Difficulté à lever le pied ou à monter les escaliers  signe d'une irritation nerveuse plus marquée.",
  },
];

const BIENFAITS = [
  {
    num: "01",
    title: "Libérer les tensions musculaires",
    desc: "Le muscle piriforme, les érecteurs du rachis et les fléchisseurs de hanche compriment souvent le nerf. L'ostéopathie relâche ces structures pour lever la pression.",
  },
  {
    num: "02",
    title: "Mobiliser le bassin et le sacrum",
    desc: "Un bassin en torsion ou un sacrum bloqué modifie le trajet du nerf sciatique. La correction de ces dysfonctions supprime une cause mécanique directe.",
  },
  {
    num: "03",
    title: "Traiter la cause, pas le symptôme",
    desc: "Hernie, contracture ou déséquilibre postural : l'ostéopathe identifie l'origine exacte et adapte son protocole pour éviter la récidive.",
  },
  {
    num: "04",
    title: "Conseils posturaux personnalisés",
    desc: "Position de sommeil, ergonomie au bureau, étirements ciblés  des gestes simples pour prolonger le bénéfice de la séance au quotidien.",
  },
];

const STEPS = [
  {
    num: "01",
    title: "Anamnèse & localisation",
    desc: "Questionnaire précis sur le trajet douloureux, les positions aggravantes et l'historique (chute, port de charge, sédentarité).",
  },
  {
    num: "02",
    title: "Tests neurologiques & posturaux",
    desc: "Test de Lasègue, mobilité du bassin, palpation des chaînes musculaires pour identifier l'origine exacte de la compression.",
  },
  {
    num: "03",
    title: "Traitement ostéopathique",
    desc: "Techniques de relâchement musculaire, mobilisation sacro-iliaque, correction des compensations posturales  doux et progressif.",
  },
  {
    num: "04",
    title: "Programme de retour à la normale",
    desc: "Exercices de stabilisation lombaire, conseils d'hygiène de vie et suivi pour éviter la rechute.",
  },
];

const Sciatique = () => (
  <>
    <SEO
      title="Sciatique & ostéopathie  Soulagement naturel à Balma"
      description="La sciatique peut être soulagée efficacement par l'ostéopathie. Maxime Nicolaieff, ostéopathe D.O. à Balma, traite les compressions du nerf sciatique par des techniques manuelles adaptées."
      path="/pathologies/sciatique"
      schema={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Pathologies",
            item: `${SITE_URL}/pathologies`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Sciatique",
            item: `${SITE_URL}/pathologies/sciatique`,
          },
        ],
      }}
    />

    {/* ── Hero ──────────────────────────────── */}
    <Box component="section" sx={SX.pageHeroSection}>
      <Box
        sx={{
          ...SX.pageHeroInner,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 340px" },
          gap: { xs: "2rem", md: "4rem" },
          alignItems: "center",
          pb: { xs: "0", md: "0" },
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Neurologique
          </Box>
          <Typography variant="h1" component="h1" sx={SX.displayTitle}>
            Sciatique <em>retrouver la mobilité.</em>
          </Typography>
          <Typography sx={SX.pageHeroSub}>
            La douleur sciatique est l'une des plaintes les plus fréquentes en
            cabinet d'ostéopathie. Dans la majorité des cas, une prise en charge
            manuelle suffit à soulager durablement sans médicament.
          </Typography>
          <Box
            sx={{ display: "flex", gap: "1rem", flexWrap: "wrap", mt: "2rem" }}
          >
            <Box
              component="a"
              href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
              target="_blank"
              rel="noreferrer"
              sx={SX.btnDark}
            >
              Prendre rendez-vous
            </Box>
            <Box component={Link} to="/pathologies" sx={SX.btnOutline}>
              Toutes les pathologies
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: { xs: "220px", md: "320px" },
            borderRadius: R.lg,
            overflow: "hidden",
            boxShadow: S.md,
            display: { xs: "none", md: "block" },
          }}
        >
          <Box
            component="img"
            src={sciatique}
            alt="Traitement de la sciatique en ostéopathie"
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      </Box>
    </Box>

    {/* ── Symptômes ─────────────────────────── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "5rem" } }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Reconnaître
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Les signes d'une <em>vraie sciatique.</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "1.25rem",
          }}
        >
          {SYMPTOMES.map((s) => (
            <Box
              key={s.title}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.5rem",
                borderLeft: `3px solid ${C.accent}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: ".92rem",
                  fontWeight: 600,
                  color: C.text,
                  mb: ".4rem",
                }}
              >
                {s.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: ".82rem",
                  color: C.textMuted,
                  lineHeight: 1.65,
                }}
              >
                {s.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Comment l'ostéopathie aide ────────── */}
    <Box
      component="section"
      sx={{ py: { xs: "3rem", md: "5.5rem" }, background: C.bgAlt }}
    >
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box component="span" sx={SX.sectionLabel}>
          Traitement
        </Box>
        <Typography
          variant="h2"
          component="h2"
          sx={{ ...SX.displayTitle, mb: "2.5rem" }}
        >
          Pourquoi l'ostéopathie <em>fonctionne ?</em>
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1.5rem",
          }}
        >
          {BIENFAITS.map((b) => (
            <Box
              key={b.num}
              sx={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: R.lg,
                padding: "1.75rem",
                display: "grid",
                gridTemplateColumns: "40px 1fr",
                gap: "1rem",
                alignItems: "start",
                transition: `box-shadow ${trans}`,
                "&:hover": { boxShadow: S.sm },
              }}
            >
              <Typography
                sx={{
                  fontFamily: F.display,
                  fontSize: "1.6rem",
                  fontWeight: 300,
                  color: C.accent,
                  lineHeight: 1,
                }}
              >
                {b.num}
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".92rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".4rem",
                  }}
                >
                  {b.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.65,
                  }}
                >
                  {b.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Déroulement ───────────────────────── */}
    <Box component="section" sx={{ py: { xs: "3rem", md: "5.5rem" } }}>
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          gap: { xs: "3rem", md: "5rem" },
          alignItems: "start",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabel}>
            Déroulement
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{ ...SX.displayTitle, mb: "1rem" }}
          >
            La prise en charge <em>en 4 temps.</em>
          </Typography>
          <Typography
            sx={{ fontSize: ".85rem", color: C.textMuted, lineHeight: 1.75 }}
          >
            La première séance dure entre 50 et 60 minutes. La majorité des
            patients ressentent un soulagement dès la première consultation.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {STEPS.map((s, i) => (
            <Box
              key={s.num}
              sx={{
                display: "grid",
                gridTemplateColumns: "48px 1fr",
                gap: "1.25rem",
                alignItems: "start",
                py: "1.5rem",
                borderBottom:
                  i < STEPS.length - 1 ? `1px solid ${C.border}` : "none",
              }}
            >
              <Typography
                sx={{
                  fontFamily: F.display,
                  fontSize: "1.8rem",
                  fontWeight: 300,
                  color: C.accent,
                  lineHeight: 1,
                  pt: ".1rem",
                }}
              >
                {s.num}
              </Typography>
              <Box>
                <Typography
                  sx={{
                    fontSize: ".92rem",
                    fontWeight: 600,
                    color: C.text,
                    mb: ".3rem",
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: ".82rem",
                    color: C.textMuted,
                    lineHeight: 1.7,
                  }}
                >
                  {s.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>

    {/* ── Attention médicale ────────────────── */}
    <Box component="section" sx={{ py: { xs: "2rem", md: "3rem" } }}>
      <Box
        sx={{ maxWidth: maxW, mx: "auto", px: { xs: "1rem", md: "2.5rem" } }}
      >
        <Box
          sx={{
            display: "flex",
            gap: ".75rem",
            alignItems: "flex-start",
            background: "#fff8f0",
            border: "1px solid #f0d0b0",
            borderRadius: R.base,
            px: "1.25rem",
            py: "1rem",
            fontSize: ".82rem",
            color: C.textMuted,
            lineHeight: 1.7,
          }}
        >
          <Box
            component="span"
            sx={{ fontSize: "1rem", flexShrink: 0, mt: ".05rem" }}
          >
            ⚠️
          </Box>
          <Box>
            Consultez un médecin en priorité si vous présentez : une perte de
            contrôle de la vessie ou des selles, une paralysie progressive, une
            douleur suite à un traumatisme violent ou une fièvre associée.{" "}
            <strong>
              Ces signes nécessitent une imagerie avant toute prise en charge
              ostéopathique.
            </strong>
          </Box>
        </Box>
      </Box>
    </Box>

    {/* ── CTA ───────────────────────────────── */}
    <Box
      component="section"
      sx={{ background: C.dark, py: { xs: "3.5rem", md: "6rem" } }}
    >
      <Box
        sx={{
          maxWidth: maxW,
          mx: "auto",
          px: { xs: "1rem", md: "2.5rem" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gap: "3rem",
          alignItems: "center",
        }}
      >
        <Box>
          <Box component="span" sx={SX.sectionLabelWhite}>
            Prendre rendez-vous
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontFamily: F.display,
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1.2,
              mb: ".75rem",
              "& em": { fontStyle: "italic", color: "rgba(255,255,255,.6)" },
            }}
          >
            Sciatique depuis trop longtemps ?<br />
            <em>Venez cette semaine.</em>
          </Typography>
          <Typography
            sx={{
              fontSize: ".85rem",
              color: "rgba(255,255,255,.55)",
              maxWidth: "420px",
              lineHeight: 1.75,
              mb: "1.5rem",
            }}
          >
            La plupart des sciatiques mécaniques se résolvent en 1 à 3 séances.
            Maxime Nicolaieff reçoit en urgence sous 24h pour les cas aigus.
          </Typography>
          <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <Box
              component="a"
              href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
              target="_blank"
              rel="noreferrer"
              sx={SX.btnAccent}
            >
              Réserver une séance
            </Box>
            <Box component="a" href="tel:0695788983" sx={SX.btnOutlineWhite}>
              06 95 78 89 83
            </Box>
          </Box>
        </Box>
        <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
          <Typography
            sx={{
              fontFamily: F.display,
              fontSize: "4.5rem",
              fontWeight: 300,
              color: "#fff",
              lineHeight: 1,
            }}
          >
            24h
          </Typography>
          <Typography
            sx={{
              fontSize: ".72rem",
              color: "rgba(255,255,255,.4)",
              mt: ".6rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            délai max
            <br />
            pour les urgences
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* ── Liens internes ────────────────────── */}
    <Box
      sx={{
        maxWidth: maxW,
        mx: "auto",
        px: { xs: "1rem", md: "2.5rem" },
        py: { xs: "2.5rem", md: "3rem" },
        display: "flex",
        gap: "1rem",
        flexWrap: "wrap",
      }}
    >
      <Box
        component={Link}
        to="/pathologies/hernie-discale"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Hernie discale →
      </Box>
      <Box
        component={Link}
        to="/pathologies/tendinite"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Tendinites →
      </Box>
      <Box
        component={Link}
        to="/pathologies"
        sx={{
          fontSize: ".85rem",
          color: C.primary,
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Toutes les pathologies →
      </Box>
    </Box>
  </>
);

export default Sciatique;
